import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
import { DTableColumnsType } from '@darwin-ui';

import {
  ACTIONS_COL_WIDTH,
  ActionsDropdown,
  CombinedText,
  DarwinFlex,
  DMenuProps,
} from '@darwin-ui';
import Icon from 'components/darwin-ui/Icon';
import { ProcessField } from 'types/dataTableTypes';
import { ColumnAction } from 'components/ui/dataTables/DataFields';
import { translateField } from 'utils/common';

/**
 * Creates base field columns that are common to both system and custom fields
 * @param t Translation function
 * @param searchQuery Optional search query for highlighting
 * @returns Array of base column definitions
 */
const createBaseFieldColumns = (
  t: TFunction,
  searchQuery?: string
): DTableColumnsType<ProcessField> => {
  return [
    {
      title: t('data-tables.fields-list.columns.field-name'),
      dataIndex: 'name',
      key: 'name',
      sorter: true,
      width: 250,
      render: (_, rowData) => {
        const isCustom = rowData.isCustomField;
        return (
          <span>
            <CombinedText
              lines={1}
              text={
                !isCustom
                  ? translateField(t, rowData.fieldKey, {
                      subKey: 'field_name',
                      by: 'key',
                    })
                  : rowData.name
              }
              highlight={searchQuery}
            />
          </span>
        );
      },
    },
    {
      title: t('data-tables.fields-list.columns.field-type'),
      dataIndex: 'type',
      key: 'type',
      sorter: true,
      render: type => {
        const typeLowerCase = type.toLocaleLowerCase();
        const text = t(`data-tables.field-types.${typeLowerCase}`);
        return <CombinedText lines={1} text={text} highlight={searchQuery} />;
      },
    },
    {
      title: t('data-tables.fields-list.columns.lenght'),
      dataIndex: 'length',
      key: 'length',
      sorter: true,
      width: 120,
      render: (length: number) => (
        <CombinedText
          lines={1}
          text={length ? String(length) : ''}
          highlight={searchQuery}
        />
      ),
    },
    {
      title: t('data-tables.fields-list.columns.description'),
      dataIndex: 'description',
      key: 'description',
      sorter: true,
      render: (_, rowData) => {
        const isCustom = rowData.isCustomField;
        return (
          <CombinedText
            lines={1}
            text={
              !isCustom
                ? translateField(t, rowData.fieldKey, {
                    subKey: 'help_text',
                    by: 'key',
                  })
                : rowData.description || ''
            }
            highlight={searchQuery}
          />
        );
      },
    },
  ];
};

/**
 * Hook for generating table columns for system fields
 * @param searchQuery Optional search query for highlighting
 * @returns Columns array for system fields
 */
export const useSystemFieldColumns = (searchQuery?: string) => {
  const { t } = useTranslation();

  return useMemo<DTableColumnsType<ProcessField>>(() => {
    return createBaseFieldColumns(t, searchQuery);
  }, [t, searchQuery]);
};

/**
 * Hook for generating table columns for custom fields
 * @param actions Array of actions to display in the actions dropdown
 * @param searchQuery Optional search query for highlighting
 * @returns Columns array for custom fields
 */
export const useCustomFieldColumns = (
  actions: ColumnAction[],
  searchQuery?: string
) => {
  const { t } = useTranslation();

  return useMemo<DTableColumnsType<ProcessField>>(() => {
    const menuItems = (field: ProcessField): DMenuProps['items'] =>
      actions.map(action => ({
        key: action.key,
        label: (
          <DarwinFlex
            gap={8}
            align="center"
            onClick={() => {
              action?.onClick?.(field);
            }}
          >
            <Icon
              name={action.name || 'darwin-user'}
              size="sm"
              color="inherit"
            />
            {action.label}
          </DarwinFlex>
        ),
      }));

    // Start with base columns
    const cols = createBaseFieldColumns(t, searchQuery);

    // Add form count column for custom fields
    cols.push({
      title: t('data-tables.fields-list.columns.form-count'),
      dataIndex: 'formCount',
      key: 'formCount',
      sorter: true,
      align: 'center',
      render: (_: unknown, rowData: ProcessField) => (
        <CombinedText
          lines={1}
          text={
            (rowData as any).formCount ? String((rowData as any).formCount) : ''
          }
          highlight={searchQuery}
        />
      ),
    });

    // Add actions column
    cols.push({
      key: 'actions',
      fixed: 'right',
      align: 'center',
      render: (_: unknown, field: ProcessField) =>
        field.isCustomField && (
          <ActionsDropdown item={field} getMenuItems={menuItems} />
        ),
      width: ACTIONS_COL_WIDTH,
    });

    return cols;
  }, [actions, searchQuery, t]);
};
